import React from 'react';

import {useRequestVersionAcceptance} from 'app/acceptance/api';
import {AttachOverlay} from 'app/general/overlays';
import {gt} from 'app/utils/i18n';

import {RequestApprovalPopover} from './RequestApprovalPopover';

import type {
  BaseDocumentVersionContentType,
  BasicUserSerializedType,
  DocumentContentType,
} from 'sweet-types';

type Props = {
  versionId: number | string;
  contentType: DocumentContentType;
  managers: Array<BasicUserSerializedType>;
  versionApprovedAt: string | null;
  disabled: boolean;
  onApprovalRequest?: (
    requestApproveFunction: () => Promise<any>
  ) => Promise<any>;
  className?: string;
  refreshData?: () => unknown;
};

export const RequestApprovalButton = ({
  versionId,
  contentType,
  onApprovalRequest,
  refreshData,
  className,
  disabled,
  versionApprovedAt,
  managers,
}: Props) => {
  const [requestVersionAcceptance] = useRequestVersionAcceptance();
  const requestConfirmation = (message: string, recipients: Array<string>) => {
    const versionContentType: BaseDocumentVersionContentType = {
      procedure: 'procedureversion' as const,
      proceduregroup: 'processversion' as const,
      policy: 'policyversion' as const,
    }[contentType];

    function requestCall(): Promise<any> {
      return requestVersionAcceptance({
        message,
        versionId,
        versionContentType,
        user_ids: recipients,
      }).unwrap();
    }

    return onApprovalRequest != null
      ? onApprovalRequest(requestCall)
      : requestCall();
  };

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <RequestApprovalPopover
          overlayProps={{...overlayProps, placement: 'bottom'}}
          managers={managers}
          contentType={contentType}
          onSubmit={requestConfirmation}
          refreshData={refreshData}
        />
      )}
    >
      {(handlers) => (
        <button
          disabled={versionApprovedAt != null || disabled}
          className={className}
          {...handlers}
        >
          {gt('Request Approval')}
        </button>
      )}
    </AttachOverlay>
  );
};
